<script setup>
// import { Swiper, SwiperSlide } from "swiper/vue";
import {
  Navigation,
  Pagination,

} from "swiper/modules";
import pako from "pako";

import productBox1 from "~/components/product-box/product-box1.vue"
import { useFilterStore } from "~/store/FilterStore";
const filterStore = useFilterStore();
const localePath = useLocalePath();
const modules = ref([Navigation, Pagination]);
// kicker/description: optional i18n keys shown around the section title.
// highlight: renders the section as a dark brand-green panel so it stands out
// (used by "Recommended for you", per cld_homepage_mockup_v3).
// linkByIds: the section has no catalog filter of its own (e.g. the personalized
// "Recommended for you" row), so "See all" opens the search page with this exact
// set of products via the multi-id `q` param (?q=168912,168914).
const props = defineProps(['products', 'att_key', 'attribute', 'category', 'sort', 'backOrders', 'categoryfilterState', 'expandedCategoryFilters', 'kicker', 'description', 'highlight', 'linkByIds'])

const productIds = computed(() =>
  (props.products || []).map((p) => p?.Identifier).filter(Boolean)
);

// "See all" only makes sense when the section maps to a filtered catalog.
const hasFilters = (props.category && props.category.length) ||
  (props.attribute && Object.keys(props.attribute).length) || props.sort ||
  (props.linkByIds && productIds.value.length);

const buildSearchUrlWithFilters = ({ attribute, category, sort, categoryfilterState, expandedCategoryFilters }) => {
  try {
    const filterParams = new URLSearchParams();

    // Add attribute filters
    if (attribute && Object.keys(attribute).length) {
      const selectedTrue = Object.fromEntries(
        Object.entries(attribute)
          .map(([key, value]) => [
            key,
            Object.entries(value)
              .filter(([_, v]) => v === true || v >= 1)
              .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {}),
          ])
          .filter(([_, value]) => Object.keys(value).length > 0)
      );

      for (const [key, inner] of Object.entries(selectedTrue)) {
        for (const [innerKey, val] of Object.entries(inner)) {
          if (val === true) {
            filterParams.append(`filter[${key}]`, innerKey);
          }
        }
      }
    }

    // Add category filters (gzip + base64 encode)
    const categoryData = {};
    if (category && category.length) {
      categoryData['category'] = category;
      if (expandedCategoryFilters && Object.keys(expandedCategoryFilters).length) {
        categoryData['expandedFilters'] = expandedCategoryFilters;
      }
      if (categoryfilterState && (
        Object.keys(categoryfilterState.child).length ||
        Object.keys(categoryfilterState.parent).length ||
        Object.keys(categoryfilterState.grandchild).length
      )) {
        categoryData['categoryfilterState'] = categoryfilterState;
      }
    }

    if (Object.keys(categoryData).length) {
      const compressedQp = pako.gzip(JSON.stringify(categoryData));
      const base64Qp = btoa(String.fromCharCode.apply(null, compressedQp));
      filterParams.append('category', base64Qp);
    }

    if (sort) {
      filterParams.append('sort', sort);
    }

    return `${localePath('/products/search')}?${filterParams.toString()}`;
  } catch (error) {
    console.error('Error building search URL with filters:', error);
    return localePath('/products/search');
  }
};

// Precomputed once so "See all" is a real link and navigation starts immediately on click.
// Id-linked sections stay reactive: the ids arrive with the (client-side) fetch.
const searchUrl = computed(() =>
  props.linkByIds
    ? `${localePath('/products/search')}?q=${productIds.value.join(',')}`
    : buildSearchUrlWithFilters(props)
);

// Pre-seed the filter store so the search page lands with the same state its URL encodes.
const applyFilters = () => {
  const { attribute, category, sort, categoryfilterState, expandedCategoryFilters } = props;
  if (attribute) {
    filterStore.setSelectedAttributeFilters(attribute)
    filterStore.setSelectedReleaseFilters({})
    filterStore.setSelectedCategoryFilters([])
  }
  if (category && category.length) {
    filterStore.setSelectedCategoryFilters(category)
    filterStore.setSelectedReleaseFilters({})
  }
  if (sort) {
    filterStore.setSelectedSort(sort)
  }

  if (categoryfilterState) {
    filterStore.setCategoryfilterState(categoryfilterState)
  }
  if (expandedCategoryFilters) {
    filterStore.setExpandedCategoryFilters(expandedCategoryFilters)
  }
};

</script>

<template>
  <div v-once :class="highlight ? 'recommend-panel rounded-3xl py-6 mb-8 text-white shadow-lg' : ''">
    <div class="container mx-auto px-4 flex justify-between items-end mb-4 gap-4">
      <div class="min-w-0">
        <p v-if="kicker" class="text-xs font-bold uppercase tracking-widest mb-1"
          :class="highlight ? 'text-[var(--color-primary-200)]' : 'text-[#008556]'">
          {{ $t(kicker) }}
        </p>
        <h3 class="text-lg font-medium">
          <NuxtLink :to="searchUrl" class="hover:underline cursor-pointer"
            :class="highlight ? 'text-white' : 'text-[#008556]'" @click="applyFilters">
            {{ $t(att_key) }}
          </NuxtLink>
        </h3>
        <p v-if="description" class="text-sm mt-1 max-w-2xl"
          :class="highlight ? 'text-white/70' : 'text-gray-500'">
          {{ $t(description) }}
        </p>
      </div>
      <NuxtLink v-if="hasFilters" :to="searchUrl" class="hover:underline text-sm cursor-pointer shrink-0"
        :class="highlight ? 'text-white/90' : 'text-[#008556]'" @click="applyFilters">
        <h6>{{ $t('see_all') }}</h6>
      </NuxtLink>
    </div>
    <div class="container mx-auto px-4">
      <div :v-if="products && products.length">
        <swiper-container :breakpoints="{
          320: { slidesPerView: 1, spaceBetween: 20 },
          480: { slidesPerView: 2, spaceBetween: 20 },
          757: { slidesPerView: 2, spaceBetween: 20 },
          991: { slidesPerView: 2, spaceBetween: 12 },
          992: { slidesPerView: 5, spaceBetween: 18 }
        }" :slidesPerView="1" :navigation="true" :grabCursor="true" :freeMode="true" :centeredSlides="false"
          :modules="modules">
          <!-- Keep API order: sliders are sorted server-side (Creation Date DESC), do not shuffle. -->
          <swiper-slide v-for="(item, i) in products" :key="i">
            <div class="w-full my-2 h-full">
              <productBox1 :item="item" :index="i" :bo="backOrders?.[item.Identifier]"></productBox1>
            </div>
          </swiper-slide>
        </swiper-container>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Dark brand-green highlight panel (mockup's "Recommended for you" card),
   built from the primary palette so it matches the button green. */
.recommend-panel {
  background: linear-gradient(120deg, #04160c, var(--color-primary-800) 62%, var(--color-primary-600));
}
</style>
